import { LoadingButton } from '@mui/lab';
import {
    Box,
    FormControl,
    InputAdornment,
    OutlinedInput,
    Stack,
    TextField,
    Typography,
    FormLabel
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { saveCustomerDischargeConfig } from '../res';
import { doc, getDoc } from 'firebase/firestore';
import { firestoreInstance } from 'config/ApiService';
import DefaultDischargeConfig from '../data/DefaultDischargeConfig';
import { useSelector } from 'react-redux';
import { logGoogleAnalyticsEvent, logScreenView } from 'utils/gaLog';

export type DischargeSettingsType = {
    dischargeAfterInDays: number;
    appOverlayHeader: string;
    appOverlayMessage: string;
    treatmentNote: string;
    customerNote: string;
    patientEmailSubject: string;
    patientEmailBody: string;
};

const DischargeSettingsForm = ({
    customerId,
    isTimeSensitive,
    dischargeCode,
    customerName
}: {
    customerId: string;
    dischargeCode: string;
    customerName: string;
    isTimeSensitive?: boolean | undefined;
}) => {
    const [isSaving, setIsSaving] = React.useState(false);
    const [state, setState] = React.useState<DischargeSettingsType>({
        dischargeAfterInDays: 0,
        appOverlayHeader: '',
        appOverlayMessage: '',
        treatmentNote: '',
        customerNote: '',
        patientEmailSubject: '',
        patientEmailBody: ''
    });

    const { enqueueSnackbar } = useSnackbar();

    const region = useSelector((state: any) => state.AuthReducer.currentRegion);
    const clinician = useSelector((state: any) => state.AuthReducer.user);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setState((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const isValidState =
        (!isTimeSensitive || state.dischargeAfterInDays > 0) &&
        state.appOverlayHeader.length <= 45 &&
        state.appOverlayMessage.length <= 200 &&
        state.treatmentNote.length <= 200 &&
        state.customerNote.length <= 200 &&
        state.patientEmailSubject.length <= 100 &&
        state.patientEmailBody.length <= 500;

    const handleSave = async () => {
        try {
            setIsSaving(true);
            await saveCustomerDischargeConfig(customerId, dischargeCode, state, region);
            logGoogleAnalyticsEvent('discharge_config_edit_save_click', {
                customer_id: customerId,
                discharge_code: dischargeCode,
                customer_name: customerName,
                region,
                logged_in_user_id: clinician.id,
                logged_in_username: clinician.name,
                logged_in_user_role: clinician.role
            });
            enqueueSnackbar('Discharge settings saved successfully', { variant: 'success' });
        } catch (err) {
            console.error('Error saving discharge settings:', err);
            enqueueSnackbar('Failed to save discharge settings', { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const callInProgress = React.useRef(false);

    React.useEffect(() => {
        (async () => {
            if (!customerId || !dischargeCode) {
                return;
            }
            if (callInProgress.current) {
                return; // Prevent multiple calls
            }
            callInProgress.current = true;
            const ref = doc(
                firestoreInstance(),
                'clients',
                customerId,
                'discharge_configs',
                dischargeCode
            );
            const docSnapshot = await getDoc(ref);
            if (!docSnapshot.exists()) {
                // load from defaults
                const defaults = DefaultDischargeConfig[dischargeCode];
                if (!defaults) {
                    return;
                }
                setState({
                    dischargeAfterInDays: defaults.discharge_after_in_days || 0,
                    appOverlayHeader: defaults.app_overlay_header || '',
                    appOverlayMessage: defaults.app_overlay_message || '',
                    treatmentNote: defaults.treatment_note || '',
                    customerNote: defaults.customer_note || '',
                    patientEmailSubject: defaults.patient_email_subject || '',
                    patientEmailBody: defaults.patient_email_body || ''
                });
                enqueueSnackbar(
                    'Using default discharge settings for this code - Please save this configuration to apply to the customer',
                    { variant: 'warning' }
                );
                return;
            }
            const data = docSnapshot.data();
            setState({
                dischargeAfterInDays: data.discharge_after_in_days || 0,
                appOverlayHeader: data.app_overlay_header || '',
                appOverlayMessage: data.app_overlay_message || '',
                treatmentNote: data.treatment_note || '',
                customerNote: data.customer_note || '',
                patientEmailSubject: data.patient_email_subject || '',
                patientEmailBody: data.patient_email_body || ''
            });
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, dischargeCode]);

    // Page view logging
    React.useEffect(() => {
        // Log screen view event
        logScreenView(`discharge_config_${dischargeCode}_view`, {
            // customer_name: state.clientName ?? 'unknown',
            customer_id: customerId ?? 'unknown',
            customer_name: customerName ?? 'unknown'
        });
    }, []);

    return (
        <Stack spacing={2} sx={{ padding: 2 }}>
            {isTimeSensitive && (
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flex: 0.5,
                            flexDirection: 'column'
                        }}
                    >
                        <Typography sx={{ fontWeight: 'bold' }} variant="subtitle1">
                            Automatic Discharge
                        </Typography>
                        <Typography variant="caption">
                            {`Scheduled after last invite ${
                                dischargeCode === 'episode_closed' ? '(Day 4)' : '(Day 8)'
                            }`}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flex: 1 }}>
                        <FormControl sx={{ m: 1, width: '25ch' }} variant="outlined">
                            <FormLabel
                                sx={{ fontSize: '0.75rem' }}
                                htmlFor="outlined-adornment-days"
                            >
                                Number of days after last reminder
                            </FormLabel>
                            <OutlinedInput
                                id="outlined-adornment-days"
                                endAdornment={<InputAdornment position="end">days</InputAdornment>}
                                aria-describedby="outlined-days-helper-text"
                                inputProps={{
                                    'aria-label': 'days',
                                    min: 1,
                                    max: 30,
                                    name: 'dischargeAfterInDays'
                                }}
                                value={state.dischargeAfterInDays}
                                onChange={handleChange}
                                type="number"
                            />
                        </FormControl>
                    </Box>
                </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', flex: 0.5, flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>App Overlay Header</Typography>
                    <Typography variant="caption">(max 45 characters)</Typography>
                </Box>
                <Box sx={{ display: 'flex', flex: 1 }}>
                    <FormControl sx={{ m: 1 }} variant="outlined" fullWidth>
                        <TextField
                            value={state.appOverlayHeader}
                            onChange={handleChange}
                            slotProps={{ htmlInput: { name: 'appOverlayHeader', maxLength: 45 } }}
                            helperText={`${state.appOverlayHeader.length}/45` + ' characters'}
                        />
                    </FormControl>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', flex: 0.5, flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>App Overlay Message</Typography>
                </Box>
                <Box sx={{ display: 'flex', flex: 1 }}>
                    <FormControl sx={{ m: 1 }} variant="outlined" fullWidth>
                        <TextField
                            multiline
                            rows={3}
                            onChange={handleChange}
                            value={state.appOverlayMessage}
                            slotProps={{ htmlInput: { name: 'appOverlayMessage' } }}
                        />
                    </FormControl>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', flex: 0.5, flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>Treatment Note</Typography>
                </Box>
                <Box sx={{ display: 'flex', flex: 1 }}>
                    <FormControl sx={{ m: 1 }} variant="outlined" fullWidth>
                        <TextField
                            multiline
                            rows={3}
                            value={state.treatmentNote}
                            onChange={handleChange}
                            slotProps={{ htmlInput: { name: 'treatmentNote' } }}
                        />
                    </FormControl>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', flex: 0.5, flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>Customer Note</Typography>
                </Box>
                <Box sx={{ display: 'flex', flex: 1 }}>
                    <FormControl sx={{ m: 1 }} variant="outlined" fullWidth>
                        <TextField
                            multiline
                            rows={3}
                            value={state.customerNote}
                            onChange={handleChange}
                            slotProps={{
                                htmlInput: {
                                    name: 'customerNote'
                                }
                            }}
                        />
                    </FormControl>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', flex: 0.5, flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>Patient Email Subject</Typography>
                </Box>
                <Box sx={{ display: 'flex', flex: 1 }}>
                    <FormControl sx={{ m: 1 }} variant="outlined" fullWidth>
                        <TextField
                            value={state.patientEmailSubject}
                            onChange={handleChange}
                            slotProps={{ htmlInput: { name: 'patientEmailSubject' } }}
                        />
                    </FormControl>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', flex: 0.5, flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>Patient Email Body</Typography>
                </Box>
                <Box sx={{ display: 'flex', flex: 1 }}>
                    <FormControl sx={{ m: 1 }} variant="outlined" fullWidth>
                        <TextField
                            multiline
                            rows={3}
                            value={state.patientEmailBody}
                            onChange={handleChange}
                            slotProps={{ htmlInput: { name: 'patientEmailBody' } }}
                        />
                    </FormControl>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                    size="large"
                    variant="contained"
                    onClick={handleSave}
                    loading={isSaving}
                    disabled={!isValidState}
                >
                    Save
                </LoadingButton>
            </Box>
        </Stack>
    );
};

export default DischargeSettingsForm;
